import React from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

/**
 * Reusable confirmation modal with OK and Cancel.
 * Use via useConfirm() hook for promise-based API (replaces window.confirm).
 *
 * Props (when used standalone):
 *   open, onClose, onConfirm, title, message,
 *   confirmLabel, cancelLabel, variant ('danger' | 'warning' | 'info')
 */
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = "Confirm",
  message = "Are you sure?",
  confirmLabel = "OK",
  cancelLabel = "Cancel",
  variant = "danger",
}) => {
  if (!open) return null;

  const handleConfirm = () => {
    onConfirm?.();
    // Caller (e.g. ConfirmContext) is responsible for closing
  };

  const handleCancel = () => {
    onClose?.();
  };

  const variantStyles = {
    danger: {
      iconBg: "bg-red-100",
      iconColor: "text-red-600",
      button: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
    },
    warning: {
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600",
      button: "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500",
    },
    info: {
      iconBg: "bg-indigo-100",
      iconColor: "text-indigo-600",
      button: "bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500",
    },
  };

  const styles = variantStyles[variant] || variantStyles.danger;

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 transition-opacity"
          aria-hidden="true"
          onClick={handleCancel}
        />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${styles.iconBg} ${styles.iconColor}`}>
              <span className="text-lg font-bold">?</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">{message}</p>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-shrink-0 rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.button}`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
